// Serialize the UI's clause-based section spec into a grouped logical tree + nodes (no flat sections)

function stableHash(str) {
  // djb2
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = (h * 33) ^ str.charCodeAt(i);
  }
  return (h >>> 0).toString(36);
}

function stableNodeId(ind) {
  const payload = {
    n: (ind?.name || '').toUpperCase(),
    p: normalizeParams(ind?.params || {}),
    i: ind?.input ? summarizeInput(ind.input) : 'price:close',
  tf: ind?.timeframe || undefined,
  };
  return `${payload.n}_${stableHash(JSON.stringify(payload))}`.toLowerCase();
}

// Map UI operator labels/symbols to backend canonical operators
// Accepts symbols ('<', '>=', '==', '!=') and UI names ('less_than', 'greater_than_or_equal', 'equals', 'not_equals', 'crosses_above', 'crosses_below')
const UI_TO_BACK_OP = {
  '<': '<',
  '<=': '<=',
  '>': '>',
  '>=': '>=',
  '==': '==',
  '!=': '!=',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
  eq: '==',
  ne: '!=',
  less_than: '<',
  less_than_or_equal: '<=',
  greater_than: '>',
  greater_than_or_equal: '>=',
  equals: '==',
  not_equals: '!=',
  crosses_above: 'cross_over',
  crosses_below: 'cross_under',
};

function toBackendOperator(op) {
  if (!op) return op;
  const key = String(op).toLowerCase();
  return UI_TO_BACK_OP[key] || op;
}

function normalizeParams(params) {
  const keys = Object.keys(params || {}).sort();
  const out = {};
  keys.forEach((k) => (out[k] = params[k]));
  return out;
}

function summarizeInput(inp) {
  if (!inp) return 'price:close';
  if (inp.type === 'price') return { t: 'price', s: inp.source || 'close', tf: inp.timeframe || undefined };
  if (inp.type === 'indicator') return { n: inp.name, p: normalizeParams(inp.params || {}), i: summarizeInput(inp.input), tf: inp.timeframe || undefined };
  return 'price:close';
}

function ensurePriceNode(nodes, source = 'close', timeframe) {
  const id = `price_${String(source).toLowerCase()}${timeframe ? '_' + String(timeframe).toLowerCase() : ''}`;
  if (!nodes.some((n) => n.id === id)) {
    nodes.push({ id, name: 'PRICE', params: { source }, inputs: undefined, timeframe });
  }
  return id;
}

function ensureIndicatorNode(nodes, ind) {
  const id = stableNodeId(ind);
  if (nodes.some((n) => n.id === id)) return id;

  // Build source first
  let sourceId;
  const input = ind.input;
  if (!input) {
    sourceId = ensurePriceNode(nodes, 'close', ind?.timeframe);
  } else if (input.type === 'price') {
    sourceId = ensurePriceNode(nodes, input.source || 'close', input.timeframe || ind?.timeframe);
  } else if (input.type === 'indicator') {
    sourceId = ensureIndicatorNode(nodes, input);
  } else {
    sourceId = ensurePriceNode(nodes, 'close', ind?.timeframe);
  }

  const node = {
    id,
    name: (ind.name || '').toUpperCase(),
    params: normalizeParams(ind.params || {}),
    inputs: { source: sourceId },
    timeframe: ind?.timeframe,
  };
  nodes.push(node);
  return id;
}


// Transform a UI operand into a backend-friendly operand.
// Rules:
// - Keep numbers as-is
// - For indicators, ensure lowercase name and normalized params
// - If indicator has a timeframe, force spec evaluation by adding `inputs: [{ ref: 'PRICE', params: { field } }]`
//   and drop any UI-only `input` field. Field defaults to 'close' or the selected price source.
function toBackendOperand(opnd) {
  if (opnd == null) return 0;
  if (typeof opnd === 'number') return opnd;
  if (typeof opnd !== 'object') return opnd;

  // Price operand: keep as-is for now
  if (opnd.type === 'price') {
    const out = { type: 'price', source: opnd.source || 'close' };
    if (opnd.timeframe) out.timeframe = opnd.timeframe;
    if (Number.isFinite(opnd.lag) && opnd.lag > 0) {
      return { op: 'LAG', n: Math.max(0, opnd.lag|0), expr: out };
    }
    return out;
  }

  // Indicator-like
  if (opnd.type === 'indicator' || opnd.name) {
    const name = String(opnd.name || '').toLowerCase();
    const params = normalizeParams(opnd.params || {});
    const out = { type: 'indicator', name, params };
    if (opnd.output) out.output = opnd.output;
    if (opnd.timeframe) out.timeframe = opnd.timeframe;
    // Map input (price or nested indicator)
    if (opnd.input) {
      if (opnd.input.type === 'price') {
        const priceField = opnd.input.source || 'close';
        out.inputs = [ { ref: 'PRICE', params: { field: priceField } } ];
      } else if (opnd.input.type === 'indicator') {
        // Inline nested indicator input in grouped spec as an inputs[] array
        // Backend adapter traverses operand.inputs (list), not singular input
        out.inputs = [ toBackendOperand(opnd.input) ];
      }
    }
    // Wrap with LAG if lag > 0
    if (Number.isFinite(opnd.lag) && opnd.lag > 0) {
      return { op: 'LAG', n: Math.max(0, opnd.lag|0), expr: out };
    }
    return out;
  }

  // Window predicate UI shape: { kind:'window_pred', op, inputRef|expr, n }
  if (opnd.kind === 'window_pred' && opnd.op && Number.isFinite(opnd.n)) {
    const expr = opnd.inputRef && typeof opnd.inputRef === 'object' ? toBackendOperand(opnd.inputRef) : opnd.inputRef || opnd.expr;
    return { op: opnd.op, expr, n: Math.max(1, opnd.n|0) };
  }

  // Expression node with lhs/rhs (comparator inside window): recursively map operands
  if (opnd.op && (opnd.lhs !== undefined && opnd.rhs !== undefined)) {
    // Normalize arithmetic aliases; otherwise map comparator operators to backend
    const raw = String(opnd.op);
    let opCanon;
    switch (raw) {
      case '+': opCanon = 'ADD'; break;
      case '-': opCanon = 'SUB'; break;
      case '*': opCanon = 'MUL'; break;
      case '/': opCanon = 'DIV'; break;
      default:
        opCanon = toBackendOperator(raw);
        break;
    }
    return { lhs: toBackendOperand(opnd.lhs), op: opCanon, rhs: toBackendOperand(opnd.rhs) };
  }
  // Expression node with expr (e.g., LAG, SHIFT): pass through but ensure inner is mapped
  if (opnd.op && opnd.expr) {
    return { ...opnd, expr: toBackendOperand(opnd.expr) };
  }

  return opnd;
}

// Preserve grouping: build a nested logical tree where each node is { op, clauses: [...] }
// Leaves are normalized to { lhs, op, rhs } and may include operand timeframe overrides
function buildGroupedTree(section) {
  if (!section) return { op: 'AND', clauses: [] };
  const op = section.op || 'AND';
  const clauses = [];
  for (const c of section.clauses || []) {
    if (!c) continue;
    if (c.type === 'group' || (c.op && Array.isArray(c.clauses))) {
      clauses.push(buildGroupedTree(c));
    } else {
    // leaf clause - transform operands for backend consumption
  const leaf = { lhs: toBackendOperand(c.lhs), op: toBackendOperator(c.op), rhs: toBackendOperand(c.rhs) };
  clauses.push(leaf);
    }
  }
  return { op, clauses };
}

export function buildGraphFromSpec(uiSpec, options = {}) {
  const nodes = [];

  // Preserve grouping: build once
  const grouped = {
    entry_long: buildGroupedTree(uiSpec.entry_long),
    entry_short: buildGroupedTree(uiSpec.entry_short),
    exit_long: buildGroupedTree(uiSpec.exit_long),
    exit_short: buildGroupedTree(uiSpec.exit_short),
  };

  // Populate nodes by walking grouped leaves and ensuring nodes exist for each operand
  const visitOperand = (opnd) => {
    if (opnd == null) return;
    if (typeof opnd === 'number') return;
    if (opnd.type === 'number') return;
    // Window predicate wrapper: visit inner expression
    if (opnd && typeof opnd === 'object' && opnd.kind === 'window_pred') {
      const inner = opnd.inputRef && typeof opnd.inputRef === 'object' ? opnd.inputRef : opnd.expr;
      visitOperand(inner);
      return;
    }
    // Comparator expression: visit lhs and rhs
    if (opnd && typeof opnd === 'object' && opnd.lhs && opnd.rhs) {
      visitOperand(opnd.lhs);
      visitOperand(opnd.rhs);
      return;
    }
    if (opnd.type === 'price') {
      ensurePriceNode(nodes, opnd.source || 'close', opnd.timeframe);
      return;
    }
    if (opnd.type === 'indicator' || opnd.name) {
      // Accept UI-shape with name/params/input
      const ind = opnd.type === 'indicator' ? opnd : { type: 'indicator', name: opnd.name, params: opnd.params, input: opnd.input, timeframe: opnd.timeframe };
      ensureIndicatorNode(nodes, ind);
      return;
    }
  };
  const walkGrouped = (node) => {
    if (!node) return;
    if (Array.isArray(node.clauses)) {
      for (const c of node.clauses) {
        if (!c) continue;
        if (c.clauses && !c.lhs) {
          walkGrouped(c);
        } else {
          visitOperand(c.lhs);
          visitOperand(c.rhs);
        }
      }
    }
  };
  walkGrouped(grouped.entry_long);
  walkGrouped(grouped.entry_short);
  walkGrouped(grouped.exit_long);
  walkGrouped(grouped.exit_short);

  // Convert UI risk (percent) to backend format (fraction) for risk_pct only.
  // Other pct fields (e.g., daily_loss_pct, SL pct value) remain as-is unless backend requires otherwise.
  const convertRiskForBackend = (risk) => {
    if (!risk) return {};
    const out = { ...risk };
    if (Number.isFinite(out.risk_pct)) {
  // convert to fraction and round to avoid binary float artifacts
  out.risk_pct = Number((out.risk_pct / 100).toFixed(10));
    }
    return out;
  };

  return {
    timeframe: options.timeframe || uiSpec.timeframe || 'M1',
    nodes,
    // grouped logical representation (preferred going forward)
    ...grouped,
    risk: convertRiskForBackend(uiSpec.risk || {}),
  };
}
